import { useCallback, useEffect, useRef, useState } from 'react';

type Setter<T> = (next: T | ((prev: T) => T)) => void;

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

/**
 * Persisted state that stays in sync across tabs.
 *
 * Storage failures (private mode, quota) degrade to plain in-memory state
 * instead of throwing — a table preference is never worth breaking a view for.
 */
export function useLocalStorage<T>(key: string, initialValue: T): [T, Setter<T>] {
  const initial = useRef(initialValue);
  const [value, setValue] = useState<T>(() => read(key, initial.current));

  // A changed key means a different preference: re-read rather than carry over.
  const previousKey = useRef(key);
  useEffect(() => {
    if (previousKey.current === key) return;
    previousKey.current = key;
    setValue(read(key, initial.current));
  }, [key]);

  const set = useCallback<Setter<T>>(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          /* keep the in-memory value */
        }
        return resolved;
      });
    },
    [key],
  );

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key || event.storageArea !== window.localStorage) return;
      setValue(read(key, initial.current));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  return [value, set];
}
